import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

const cardVariants = cva('bg-surface border-border rounded-xl border', {
  variants: {
    padding: {
      none: '',
      sm: 'p-4',
      md: 'p-5',
      lg: 'p-6',
    },
    elevation: {
      flat: '',
      raised: 'shadow-sm',
      floating: 'shadow-lg',
    },
    /** Wyroznienie w tonacji marki — jak panel „MECZE W TRAKCIE” w makiecie. */
    accent: {
      none: '',
      brand: 'border-brand-200 bg-brand-subtle',
    },
  },
  defaultVariants: {
    padding: 'md',
    elevation: 'flat',
    accent: 'none',
  },
});

export interface CardProps extends ComponentPropsWithRef<'div'>, VariantProps<typeof cardVariants> {}

/** Kontener tresci — podstawowy blok budulcowy wszystkich ekranow. */
export function Card({ className, padding, elevation, accent, ...props }: CardProps) {
  return (
    <div className={cn(cardVariants({ padding, elevation, accent }), className)} {...props} />
  );
}

export interface CardHeaderProps extends Omit<ComponentPropsWithRef<'div'>, 'title'> {
  /** Tytul sekcji — w makietach pisany wersalikami, mniejszym stopniem. */
  title?: ReactNode;
  /** Element po prawej stronie naglowka (filtr, link „Zobacz wszystkie”). */
  action?: ReactNode;
}

/** Naglowek karty z opcjonalna akcja po prawej stronie. */
export function CardHeader({ className, title, action, children, ...props }: CardHeaderProps) {
  return (
    <div className={cn('mb-4 flex items-center justify-between gap-4', className)} {...props}>
      {title ? (
        <h3 className="text-fg-muted text-xs font-semibold tracking-[0.06em] uppercase">{title}</h3>
      ) : (
        children
      )}
      {action}
    </div>
  );
}

/** Stopka karty — miejsce na akcje. */
export function CardFooter({ className, ...props }: ComponentPropsWithRef<'div'>) {
  return (
    <div
      className={cn('border-border mt-4 flex items-center gap-3 border-t pt-4', className)}
      {...props}
    />
  );
}
